import { useTheme } from "@hooks/useTheme";
import { formatDateTime } from "@utils/common";
import { PrimaryButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";

const VerificationFileDetailsModal = ({
  isOpen,
  onClose,
  file,
  "data-testid": dataTestId = "verification-file-details-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();

  if (!isOpen || !file) return null;

  const details = [
    { key: "project-name", label: t("project.project-name"), value: file.projectName || "-" },
    { key: "version", label: t("project.verification.version"), value: file.verificationVersion || "-" },
    { key: "requester", label: t("common.requester"), value: file.createrName || "-" },
    { key: "requested-at", label: t("common.requested-at"), value: file.createdAt ? formatDateTime(file.createdAt) : "-" },
    { key: "verification-status", label: t("project.verification.verification-status"), value: file.status || "Ready", isStatus: true },
    { key: "verification-file-name", label: t("project.verification.verification-file-name"), value: file.realFileName || "-" },
  ];

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-center backdrop-blur-[1px]`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`${colors.bgModal} w-[90vw] max-w-3xl max-h-[90vh] flex-shrink-0 overflow-auto rounded-xl shadow-xl`}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`px-6 pt-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {t("project.verification.verification-details")}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {/* Details Table Container */}
          <div className={`rounded-xl border ${colors.borderPrimary} overflow-hidden`}>
            <div className="space-y-0">
              {details.map((detail, index) => (
                <div
                  key={detail.key}
                  className={`flex items-center py-3 ${index % 2 === 0 ? colors.bgInput : ""}`}
                  data-testid={fieldContainerTestId(detail.key)}
                >
                  <div className={`w-1/3 text-sm font-medium ${colors.textSubTitle} px-4`} data-testid={fieldLabelTestId(detail.key)}>
                    {detail.label}:
                  </div>
                  <div className="w-2/3 text-sm px-4" data-testid={fieldValueTestId(detail.key)}>
                    {detail.isStatus ? (
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${colors.bgSuccessBright}`}></div>
                        <span className={`${colors.textPrimary} ${colors.textSuccessBright}`}>{detail.value}</span>
                      </div>
                    ) : (
                      <span className={colors.textPrimary}>{detail.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Information Section */}
          {file.description && (
            <div className={`mt-4 pt-4 border-t ${colors.borderPrimary}`} data-testid={fieldContainerTestId("description")}>
              <div className={`rounded-lg border ${colors.borderPrimary} overflow-hidden`}>
                <div className={`flex items-start py-3 ${colors.bgSecondary} px-4`}>
                  <div className={`w-1/3 text-sm font-medium ${colors.textSubTitle}`} data-testid={fieldLabelTestId("description")}>
                    {t("project.verification.description")}:
                  </div>
                  <div className="w-2/3 text-sm" data-testid={fieldValueTestId("description")}>
                    <span className={colors.textPrimary}>{file.description}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end space-x-3">
            <PrimaryButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>
              {t("common.close-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationFileDetailsModal;
